import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "../../../hooks/useSingleton";
import Svg from "../../Svg/Svg";
import getOptions from "../../../utils/getOptions";
import { components, GroupBase, MultiValueGenericProps, OptionProps } from "react-select";
import { DropdownSelect, FieldWrapper, FieldActionWrapper } from "@factorearth/component-library";
import { FieldInstance, FieldTemplate, Option } from "db/definitions";
import { HandleChangeType } from "hooks/useField";
import { recordSentry } from "utils/recordSentry";

interface SelectOption {
	label: string;
	value: string;
	id: string;
}

interface Props {
	fieldInstance: FieldInstance;
	fieldTemplate: FieldTemplate;
	fromTable?: boolean;
	handleChange: (e: HandleChangeType) => Promise<void>;
	online: boolean;
	value: string;
}

/**
 * Dropdown Field Type
 */
function DropdownField(props: Props) {
	const { fieldInstance, fieldTemplate, fromTable, handleChange, online, value } = props;

	// this is the delimiter for all the selected options' ids in the value
	const delim = String.fromCharCode(31);
	const [loaded, setLoaded] = useState(false);
	const [currentTemplate, setCurrentTemplate] = useState(fieldTemplate);
	const [optionMap, setOptionMap] = useState<Record<string, Option>>({});
	const justSelectedRef = useRef(false);
	const color = useTheme();

	useEffect(() => {
		async function handlePaginationAndSetup(template: FieldTemplate) {
			const optionRequestData = await getOptions(template);
			const newTemplate = { ...fieldTemplate, options: { items: optionRequestData } };
			setCurrentTemplate(newTemplate);
			setOptionMap(setupOptionsMap(optionRequestData));
			setLoaded(true);
		}
		if (!fieldTemplate.options) return;
		if (fieldTemplate.options.nextToken && online) {
			handlePaginationAndSetup(fieldTemplate);
		} else {
			setOptionMap(setupOptionsMap(fieldTemplate.options.items));
			setLoaded(true);
		}
	}, []);

	function setupOptionsMap(options: Option[]) {
		const map: Record<string, Option> = {};
		for (let i = 0; i < options.length; i++) {
			const option = options[i];
			let id = option.id;
			if (fieldInstance.altInstanceId && option.linkedId) {
				// it is a linked field and so the linkedId on the option must be used instead of the id.
				id = option.linkedId;
			}
			map[id] = option;
		}
		return map;
	}

	function buildOptions() {
		const map: Map<string, SelectOption> = new Map();
		const options: SelectOption[] = [];
		// Build out the fieldInstance .field template options
		if (!currentTemplate.options?.items || !currentTemplate.optionsOrder) return options;
		for (let i = 0; i < currentTemplate.options.items.length; i++) {
			const option = currentTemplate.options.items[i];
			let id = option.id;
			if (fieldInstance.altInstanceId && option.linkedId) {
				// it is a linked field and so the linkedId on the option must be used instead of the id.
				id = option.linkedId;
			}

			const optionData = {
				label: option.label,
				value: option.label,
				id,
			};

			// this map is for the ordering so must use the main id
			map.set(option.id, optionData);
		}

		// do the optionsorder
		for (let i = 0; i < currentTemplate.optionsOrder.length; i++) {
			const optionId = currentTemplate.optionsOrder[i];
			const optionData = map.get(optionId);
			if (optionData) {
				options.push(optionData);
			} else {
				recordSentry({
					error: new Error(
						`DropdownField: Option with id ${optionId} not found in options map. FieldTemplate: ${currentTemplate.id} FormTemplate: ${currentTemplate.formTemplate}, Field: ${fieldInstance.id} (${currentTemplate.header})`,
					),
					msg: "DropdownField option not found",
				});
			}
		}

		return options;
	}

	/**
	 * Dropdown field on change handler
	 * @param e event data
	 */
	function handleDropdownChange(e: SelectOption[] | SelectOption) {
		let valueString = "";
		if (Array.isArray(e)) {
			for (let i = 0; i < e.length; i++) {
				const selectedOption = e[i];
				// Custom options do not have ids on them. Therefore, we use the label
				const valueToUse = selectedOption.id || selectedOption.label;
				valueString += i === e.length - 1 ? valueToUse : `${valueToUse}${delim}`;
			}
		} else {
			valueString = e.id;
		}

		const data = {
			target: {
				value: valueString,
			},
		};

		handleChange(data);
	}

	/**
	 * setup the selected string value into objects for the dropdown
	 * @returns selected values as dropdown option objects
	 */
	function buildValue() {
		if (!value || value === "") {
			return [];
		}
		const idArray = value.split(delim);
		let values = [];

		for (let i = 0; i < idArray.length; i++) {
			const id = idArray[i];
			if (id === "") {
				continue;
			}
			const option = optionMap[id];
			option ? values.push({ id, value: option.label, label: option.label }) : values.push({ id, value: id, label: id });
		}

		return values;
	}

	const CustomOption = (props: OptionProps<unknown, boolean, GroupBase<unknown>>) => {
		return (
			// @ts-ignore
			<span id={`${props.selectProps["classNamePrefix"]}-option-${props.data.id}`} key={props.innerProps.key}>
				<components.Option {...props} />
			</span>
		);
	};

	const CustomMultiValueContainer = (props: MultiValueGenericProps<unknown, boolean, GroupBase<unknown>>) => {
		return (
			<span id={`${props.selectProps["classNamePrefix"]}-multi-value-${props.data.id}`}>
				<components.MultiValueContainer {...props} />
			</span>
		);
	};

	return (
		<FieldWrapper
			colorPalette={color}
			label={!fromTable ? fieldTemplate.header : undefined}
			link={fieldInstance.altInstanceId ? <Svg icon="LINKED" /> : undefined}
			data-fieldtemplateid={fieldTemplate.id}>
			<FieldActionWrapper>
				<DropdownSelect
					name={currentTemplate.header}
					id={fieldInstance.id}
					isMulti={!fieldTemplate.action?.includes("SINGLE_SELECT")}
					// This is forcing the onInputChange to trigger when someone selects an option without typing in the field
					blurInputOnSelect={true}
					value={loaded ? buildValue() : []}
					onChange={(selected, meta) => {
						if (meta.action === "select-option" || meta.action === "create-option") {
							justSelectedRef.current = true;
						}
						// @ts-ignore
						handleDropdownChange(selected);
					}}
					onInputChange={(_val, action) => {
						if (action.action === "input-blur" && action.prevInputValue !== "") {
							if (justSelectedRef.current) {
								justSelectedRef.current = false;
								return;
							}
							const currentOptions = value.split(delim).map((id) => {
								const selectedOption = optionMap[id];
								if (selectedOption?.value) return { label: selectedOption.label, value: selectedOption.value, id: selectedOption.id };
								return { label: id, value: id, id: id };
							});
							currentOptions.push({ label: action.prevInputValue, value: action.prevInputValue, id: action.prevInputValue });
							handleDropdownChange(currentOptions);
						} else if (action.action === "set-value") {
							justSelectedRef.current = true;
						} else if (action.action === "input-change") {
							justSelectedRef.current = false;
						}
					}}
					options={buildOptions()}
					isSearchable={!fieldTemplate.action?.includes("DISABLE_KEYBOARD")}
					menuPlacement="auto"
					minMenuHeight={300}
					menuPortalTarget={document.body}
					// @ts-ignore
					createAble={currentTemplate.action?.includes("ALLOW_NEW_OPTIONS")}
					colorPalette={color}
					classNamePrefix={fieldTemplate.id}
					components={{
						Option: (props) => <CustomOption {...props} />,
						MultiValueContainer: (props) => <CustomMultiValueContainer {...props} />,
					}}
				/>
			</FieldActionWrapper>
		</FieldWrapper>
	);
}

export default DropdownField;
